import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { ItemId, ProductId, UnlockId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { ItemDef } from '@/core/content/schemas/items';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave } from '@/core/persistence/schema/v1';
import type { HandlerEnv } from './placement/handlers';
import { acquireItem } from './rewards';
import { storyStage } from './neighborhood';

/** Whether an item can be bought from the catalog right now (has a catalog source and is unlocked). */
export function catalogAvailability(
  content: ContentRegistry,
  s: GameSave,
  def: ItemDef,
): { kind: 'buyable'; price: number } | { kind: 'locked'; unlockId: string } | { kind: 'notSold' } {
  if (!def.sources.some((src) => src.kind === 'catalog') || def.buyPrice === undefined)
    return { kind: 'notSold' };
  if (def.storyGate && storyStage(s, def.storyGate.storyId) < def.storyGate.stage)
    return { kind: 'locked', unlockId: `neighborhood:${def.storyGate.storyId}` };
  if (def.unlockId && content.unlocks.size > 0 && !s.unlockedIds.includes(def.unlockId)) {
    return { kind: 'locked', unlockId: def.unlockId };
  }
  return { kind: 'buyable', price: def.buyPrice };
}

function spend(
  draft: GameSave,
  env: HandlerEnv,
  cost: number,
  kind: 'catalog' | 'stock',
  txId: string,
  refs: Record<string, string | number>,
): DomainEvent {
  draft.wallet.coins -= cost;
  if (kind === 'catalog') draft.shop.ledgerSummary.catalogSpentTotal += cost;
  else draft.shop.ledgerSummary.stockSpentTotal += cost;
  env.ledger?.push({
    txId,
    saveId: draft.saveId,
    atWallMs: env.nowWallMs,
    atPlayMs: draft.clock.playTimeMs,
    kind,
    coinsDelta: -cost,
    balanceAfter: draft.wallet.coins,
    refs,
  });
  return { type: 'COINS_CHANGED', delta: -cost, balance: draft.wallet.coins, reason: kind, ref: txId };
}

/**
 * Catalog entries to badge "Mới" (ux-screens §3.6): sold items and wallpapers/floors behind an unlock that is
 * now open and that the player has not looked at since. Starter stock (no unlock, or one granted from the
 * first boot because it has no prerequisites) is never "new".
 */
export function catalogNewIds(content: ContentRegistry, s: GameSave): string[] {
  const open = new Set<string>(s.unlockedIds);
  const seen = new Set(s.catalogSeen);
  const earned = (unlockId: UnlockId | undefined, id: string) =>
    unlockId !== undefined &&
    open.has(unlockId) &&
    (content.unlocks.get(unlockId)?.prerequisites.length ?? 0) > 0 &&
    !seen.has(id);
  const out: string[] = [];
  for (const def of content.items.values())
    if (
      def.sources.some((x) => x.kind === 'catalog') &&
      (def.storyGate
        ? storyStage(s, def.storyGate.storyId) >= def.storyGate.stage &&
          !seen.has(def.id) &&
          catalogAvailability(content, s, def).kind === 'buyable'
        : earned(def.unlockId, def.id))
    )
      out.push(def.id);
  for (const def of content.wallFloors.values())
    if (
      def.storyGate
        ? storyStage(s, def.storyGate.storyId) >= def.storyGate.stage &&
          !seen.has(def.id) &&
          (!def.unlockId || open.has(def.unlockId))
        : earned(def.unlockId, def.id)
    )
      out.push(def.id);
  return out;
}

export function markCatalogSeen(draft: GameSave, cmd: CommandOf<'markCatalogSeen'>): CommandResult {
  const seen = new Set(draft.catalogSeen);
  const fresh = cmd.ids.filter((id) => !seen.has(id));
  if (fresh.length > 0) draft.catalogSeen = [...draft.catalogSeen, ...fresh];
  return { ok: true, events: [] };
}

/** Buy decor / furniture / shop fixtures from the catalog (M2-02). Never goes below zero. */
export function buyItem(draft: GameSave, cmd: CommandOf<'buyItem'>, env: HandlerEnv): CommandResult {
  const def = env.content.items.get(cmd.itemId);
  if (!def || def.kind === 'product') return { ok: false, error: 'NOT_FOUND' };
  if (!Number.isInteger(cmd.qty) || cmd.qty < 1 || cmd.qty > 10)
    return { ok: false, error: 'INVALID_ARGUMENT' };
  const a = catalogAvailability(env.content, draft, def);
  if (a.kind === 'notSold') return { ok: false, error: 'NOT_FOUND' };
  if (a.kind === 'locked') return { ok: false, error: 'LOCKED', detail: { unlockId: a.unlockId } };
  const cost = a.price * cmd.qty;
  if (draft.wallet.coins < cost) return { ok: false, error: 'INSUFFICIENT_COINS', detail: { cost } };
  const coins = spend(draft, env, cost, 'catalog', cmd.txId, { itemId: def.id, qty: cmd.qty });
  const acquired = acquireItem(draft, def.id, cmd.qty, 'purchase', cmd.txId);
  const instanceIds = acquired.type === 'ITEM_ACQUIRED' ? acquired.instanceIds : [];
  return {
    ok: true,
    events: [
      coins,
      { type: 'ITEM_PURCHASED', txId: cmd.txId, itemId: def.id, qty: cmd.qty, cost, instanceIds },
      acquired,
    ],
  };
}

/** Restock: products come in boxes of `stockBoxSize` units into the bag (economy §2.1). */
export function buyStock(draft: GameSave, cmd: CommandOf<'buyStock'>, env: HandlerEnv): CommandResult {
  const def = env.content.items.get(cmd.itemId);
  if (def?.kind !== 'product' || def.buyPrice === undefined) return { ok: false, error: 'NOT_FOUND' };
  if (!Number.isInteger(cmd.boxes) || cmd.boxes < 1 || cmd.boxes > 10)
    return { ok: false, error: 'INVALID_ARGUMENT' };
  const a = catalogAvailability(env.content, draft, def);
  if (a.kind === 'locked') return { ok: false, error: 'LOCKED', detail: { unlockId: a.unlockId } };
  const units = cmd.boxes * env.content.balance.stockBoxSize;
  const cost = def.buyPrice * units;
  if (draft.wallet.coins < cost) return { ok: false, error: 'INSUFFICIENT_COINS', detail: { cost } };
  const coins = spend(draft, env, cost, 'stock', cmd.txId, { itemId: def.id, boxes: cmd.boxes });
  const pid = def.id as ProductId;
  draft.inventory.productStacks[pid] = (draft.inventory.productStacks[pid] ?? 0) + units;
  return {
    ok: true,
    events: [
      coins,
      { type: 'STOCK_PURCHASED', txId: cmd.txId, itemId: pid, boxes: cmd.boxes, units, cost },
      {
        type: 'ITEM_ACQUIRED',
        itemId: pid as ItemId,
        qty: units,
        instanceIds: [],
        acquiredBy: 'purchase',
        ref: cmd.txId,
      },
    ],
  };
}

export function buyWallFloor(
  draft: GameSave,
  cmd: CommandOf<'buyWallFloor'>,
  env: HandlerEnv,
): CommandResult {
  const def = env.content.wallFloors.get(cmd.wallFloorId);
  if (!def) return { ok: false, error: 'NOT_FOUND' };
  if (draft.ownedWallFloors.includes(def.id)) return { ok: false, error: 'ALREADY_CLAIMED' };
  if (def.storyGate && storyStage(draft, def.storyGate.storyId) < def.storyGate.stage)
    return { ok: false, error: 'LOCKED' };
  if (def.unlockId && env.content.unlocks.size > 0 && !draft.unlockedIds.includes(def.unlockId)) {
    return { ok: false, error: 'LOCKED', detail: { unlockId: def.unlockId } };
  }
  const cost = def.price ?? 0;
  if (draft.wallet.coins < cost) return { ok: false, error: 'INSUFFICIENT_COINS', detail: { cost } };
  const events: DomainEvent[] = [];
  if (cost > 0) events.push(spend(draft, env, cost, 'catalog', cmd.txId, { wallFloorId: def.id }));
  draft.ownedWallFloors.push(def.id);
  events.push({ type: 'WALLFLOOR_PURCHASED', txId: cmd.txId, wallFloorId: def.id, cost });
  return { ok: true, events };
}

export function applyWallFloor(
  draft: GameSave,
  cmd: CommandOf<'applyWallFloor'>,
  env: HandlerEnv,
): CommandResult {
  const def = env.content.wallFloors.get(cmd.wallFloorId);
  if (!def) return { ok: false, error: 'NOT_FOUND' };
  if (!draft.ownedWallFloors.includes(def.id)) return { ok: false, error: 'LOCKED' };
  const room = draft.rooms[cmd.roomId];
  if (def.kind === 'wall') room.wallId = def.id;
  else room.floorId = def.id;
  return { ok: true, events: [{ type: 'WALLFLOOR_APPLIED', roomId: cmd.roomId, wallFloorId: def.id }] };
}
